import { useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { OverlayPanel } from 'primereact/overlaypanel'

// Copy-link button + its OverlayPanel popover + the copyState machine, plus
// the Email-link button - identical across the portal and public Layout
// Builder shells. Everything that differs between the two shells (styling
// hooks, an optional leading action, and what clicking Email-link should do)
// is parameterized rather than hardcoded here. The Email dialog itself, and
// closing it, stay owned by each parent - this component only forwards
// emailButtonRef so the parent can refocus the trigger when its dialog closes.
export default function LayoutShareControls ({
  link,
  accessWarning,
  accessWarningClassName,
  popoverClassName,
  popoverStyle,
  containerClassName,
  leadingActions,
  onEmailLink,
  emailButtonRef,
  onExport,
  exportBlocker = null
}) {
  const [copyState, setCopyState] = useState('idle')
  const [exportAttempted, setExportAttempted] = useState(false)
  const copyOverlayRef = useRef(null)

  const handleCopyLink = async () => {
    if (!link) return
    setCopyState('copying')
    try {
      await navigator.clipboard.writeText(link)
      setCopyState('copied')
      copyOverlayRef.current?.hide()
    } catch {
      setCopyState('error')
    }
  }

  // A layout with an unnamed integration or flow is not exported; the click says what needs a name instead.
  const handleExport = () => {
    if (exportBlocker) {
      setExportAttempted(true)
      return
    }
    setExportAttempted(false)
    onExport()
  }

  return (
    <div className={containerClassName}>
      {leadingActions}
      <Button
        label={copyState === 'copied' ? 'Copied' : 'Copy link'}
        icon={copyState === 'copied' ? 'pi pi-check' : 'pi pi-copy'}
        outlined
        size='small'
        onClick={event => copyOverlayRef.current?.toggle(event)}
      />
      <Button
        ref={emailButtonRef}
        label='Email link'
        icon='pi pi-envelope'
        outlined
        size='small'
        onClick={onEmailLink}
      />
      {onExport && (
        <Button
          label='Export layout'
          icon='pi pi-download'
          outlined
          size='small'
          onClick={handleExport}
        />
      )}
      {exportAttempted && exportBlocker && <small role='alert' className='text-danger'>{exportBlocker}</small>}
      <OverlayPanel ref={copyOverlayRef} className={popoverClassName} style={popoverStyle}>
        <div role='dialog' aria-label='Link sharing notice'>
          <strong>Share access with this link</strong>
          <p className={accessWarningClassName}>{accessWarning}</p>
          {copyState === 'error' && <Message severity='error' text='Could not copy the link. Try again.' />}
          <Button
            label={copyState === 'copying' ? 'Copying…' : 'Copy link'}
            icon='pi pi-copy'
            size='small'
            onClick={handleCopyLink}
            disabled={copyState === 'copying'}
          />
        </div>
      </OverlayPanel>
    </div>
  )
}
